// src/pages/expert/RapportValorisationAdvisor.jsx
// Workflow ARTKIV : œuvres → artistes A/B/C → fourchettes artiste/médium/format → fiches œuvres → ajustements → sauvegardes

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabaseClient";
import jsPDF from "jspdf";
import { valuationFooter } from "../../utils/valuationDisclosure";
import { withoutProposalWorks } from "../../lib/proposalExclusion";

const FINALITES = [
  { key: "assurance", label: "Assurance", description: "Valeur documentée pour couverture ou renouvellement d’assurance." },
  { key: "succession", label: "Succession / transmission", description: "Valorisation prudente, explicable et traçable pour partage patrimonial." },
  { key: "marche", label: "Marché / arbitrage", description: "Fourchette de marché exploitable pour décision de vente, conservation ou acquisition." },
  { key: "pret", label: "Nantissement / prêt", description: "Lecture prudente intégrant liquidité, documentation et risque." },
];

const VALUATION_METHODS = {
  direct_cup: {
    group: "Groupe A",
    label: "CUP directe",
    methodKey: "semblables",
    short: "Marché secondaire documenté",
    description: "L’artiste dispose de ventes comparables directes suffisantes. On travaille par artiste, médium et format.",
  },
  analogy_cup: {
    group: "Groupe B",
    label: "CUP par analogie",
    methodKey: "semblables",
    short: "Comparaison d’artistes équivalents",
    description: "Les ventes directes sont insuffisantes. On rapproche la carrière de l’artiste de profils cotés comparables.",
  },
  point_method: {
    group: "Groupe C",
    label: "Méthode à points",
    methodKey: "point",
    short: "Marché peu liquide",
    description: "Le marché est peu documenté. Formule : (H cm + L cm) × valeur du point selon le médium.",
  },
};

const POINT_PRESETS = {
  oil_painting: { label: "Peinture huile", low: 12, high: 18 },
  photography: { label: "Photographie", low: 8, high: 12 },
  watercolor: { label: "Aquarelle", low: 6, high: 10 },
  drawing: { label: "Dessin", low: 5, high: 8 },
  mixed: { label: "Technique mixte", low: 8, high: 14 },
  sculpture: { label: "Sculpture / volume", low: 10, high: 18 },
  other: { label: "Autre médium", low: 8, high: 12 },
};

const VALUATION_RULES = {
  direct_cup: {
    minReferences: 3,
    ownRecentWeight: 0.60,
    own3to5Weight: 0.30,
    galleryRole: "borne_haute",
  },
  analogy_cup_with_own: {
    minReferences: 5,
    ownRecentWeight: 0.40,
    own3to5Weight: 0.20,
    comparableRecentWeight: 0.30,
    comparable3to5Weight: 0.10,
    galleryRole: "borne_haute",
  },
  analogy_cup_without_own: {
    minReferences: 5,
    comparableRecentWeight: 0.60,
    comparable3to5Weight: 0.25,
    prudenceDiscountPct: 15,
    galleryRole: "borne_haute",
  },
  point_method: {
    formulaWeight: 1,
    galleryRole: "borne_haute",
  },
};

const CONDITION_OPTIONS = [
  { key: "excellent", label: "Excellent / Très bon", adjustment: 0, active: true },
  { key: "bon", label: "Bon", adjustment: 0, active: true },
  { key: "moyen", label: "Moyen", adjustment: -20, active: true },
  { key: "tres_moyen", label: "Très moyen", adjustment: -30, active: true },
  { key: "tres_abimee", label: "Très abîmée", adjustment: -50, active: true },
  { key: "irreparable", label: "Irréparable / perte", adjustment: -100, active: false },
];

const SOURCES = [
  { key: "artprice", label: "Artprice" },
  { key: "artnet", label: "Artnet" },
  { key: "sothebys", label: "Sotheby’s" },
  { key: "christies", label: "Christie’s" },
  { key: "drouot", label: "Drouot" },
  { key: "bonhams", label: "Bonhams" },
  { key: "primaryGallery", label: "Galerie / marché primaire" },
  { key: "privateMarket", label: "Marché privé" },
  { key: "insuranceReport", label: "Rapport d’assurance" },
  { key: "other", label: "Autre" },
];

const ARTWORK_SELECT_FIELDS = [
  "id",
  "title",
  "artist",
  "year",
  "medium",
  "medium_category",
  "medium_family_key",
  "medium_display_override",
  "typeOfArt",
  "dimensions",
  "height",
  "width",
  "depth",
  "dimensions_height",
  "dimensions_width",
  "dimensions_depth",
  "specifications",
  "condition",
  "conditionNotes",
  "provenance",
  "documents",
  "certificate_auth",
  "certificate_blockchain",
  "invoice",
  "estimated_value",
  "valuation_estimated_value",
  "valuation_market_value",
  "valuation_consensus",
  "valuation_confidence_score",
  "current_estimated_value",
  "price_low",
  "price_high",
  "valuation_low",
  "valuation_high",
  "valuation_method",
  "valuation_validated_by",
  "expert_method",
  "expert_note",
  "expert_date",
  "cup_comparables",
  "last_estimation_date",
  "last_valuation_date",
  "inventory_number",
  "collection_id",
  "client_id",
].join(",");

function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function normalizeKey(value) {
  return normalizeText(value)
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeArtistName(name) {
  return normalizeKey(name || "artiste non renseigne");
}

function normalizeMediumKey(medium) {
  return normalizeKey(medium || "medium non renseigne");
}

function displayArtistName(name) {
  const raw = String(name || "").trim();
  if (!raw) return "Artiste non renseigné";
  if (raw === raw.toUpperCase() && raw.length > 3) {
    return raw
      .toLowerCase()
      .split(/\s+/)
      .map((part) => (part ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : part))
      .join(" ");
  }
  return raw;
}

function formatEuro(value) {
  const n = toNumber(value);
  if (n === null) return "—";
  return `${Math.round(n).toLocaleString("fr-FR")} €`;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function getDimension(artwork = {}, key) {
  const specs = artwork.specifications && typeof artwork.specifications === "object" ? artwork.specifications : {};
  const d = specs.dimensions && typeof specs.dimensions === "object" ? specs.dimensions : {};
  if (key === "height") return toNumber(artwork.height ?? artwork.dimensions_height ?? d.height);
  if (key === "width") return toNumber(artwork.width ?? artwork.dimensions_width ?? d.width);
  return toNumber(artwork.depth ?? artwork.dimensions_depth ?? d.depth);
}

function getEntryHeight(entry = {}) {
  return toNumber(entry.height ?? entry.dimensions_height ?? entry.specifications?.dimensions?.height);
}

function getEntryWidth(entry = {}) {
  return toNumber(entry.width ?? entry.dimensions_width ?? entry.specifications?.dimensions?.width);
}

function getSizeBucket(height, width) {
  const h = toNumber(height);
  const w = toNumber(width);
  const maxSide = Math.max(h || 0, w || 0);
  if (!maxSide) return "format non documenté";
  if (maxSide < 60) return "petit format";
  if (maxSide < 120) return "moyen format";
  if (maxSide < 200) return "grand format";
  return "très grand format";
}

function getMediumLabel(artwork = {}) {
  return (
    artwork.medium_display_override ||
    artwork.medium_category ||
    artwork.medium_family_key ||
    artwork.medium ||
    artwork.typeOfArt ||
    "Médium non renseigné"
  );
}

function inferPointPresetKey(medium = "") {
  const text = normalizeText(medium);
  if (text.includes("huile") || text.includes("oil") || text.includes("painting") || text.includes("peinture")) return "oil_painting";
  if (text.includes("photo") || text.includes("tirage") || text.includes("argentique") || text.includes("lambda")) return "photography";
  if (text.includes("aquarelle") || text.includes("watercolor")) return "watercolor";
  if (text.includes("dessin") || text.includes("drawing") || text.includes("fusain") || text.includes("crayon")) return "drawing";
  if (text.includes("sculpt") || text.includes("volume") || text.includes("bronze") || text.includes("fer") || text.includes("iron") || text.includes("acier")) return "sculpture";
  if (text.includes("mixte") || text.includes("mixed")) return "mixed";
  return "other";
}

function normalizeConditionKey(value) {
  const text = normalizeText(value);
  if (!text) return "bon";
  if (text.includes("irreparable") || text.includes("perte") || text.includes("destroy") || text.includes("loss")) return "irreparable";
  if (text.includes("tres abime") || text.includes("very damaged") || text.includes("severe")) return "tres_abimee";
  if (text.includes("tres moyen") || text.includes("poor")) return "tres_moyen";
  if (text.includes("moyen") || text.includes("fair") || text.includes("average")) return "moyen";
  if (text.includes("excellent") || text.includes("tres bon") || text.includes("very good")) return "excellent";
  if (text.includes("bon") || text.includes("good")) return "bon";
  return "bon";
}

function getConditionOption(key) {
  return CONDITION_OPTIONS.find((option) => option.key === key) || CONDITION_OPTIONS[1];
}

function inferDocFlags(artwork = {}) {
  const specs = artwork.specifications && typeof artwork.specifications === "object" ? artwork.specifications : {};
  const text = normalizeText(`${artwork.documents || ""} ${artwork.provenance || ""} ${specs.documents || ""} ${specs.certificates || ""}`);
  return {
    hasAuthenticityCertificate: Boolean(artwork.certificate_auth || text.includes("certificat") || text.includes("certificate")),
    hasBlockchainCertificate: Boolean(artwork.certificate_blockchain),
    hasArtistProvenance: Boolean(text.includes("depuis l'artiste") || text.includes("depuis l’artiste") || text.includes("from the artist") || text.includes("atelier")),
    hasImpeccableFollowUp: Boolean(text.includes("suivi impeccable") || text.includes("historique complet") || text.includes("complete provenance") || text.includes("condition report")),
  };
}

function computeConditionAdjustedValue(entry = {}) {
  const retained = toNumber(entry.valeurRetenue);
  const low = toNumber(entry.valeurBasse);
  const high = toNumber(entry.valeurHaute);
  const referenceValue = retained || (low && high ? Math.round((low + high) / 2) : low || high || 0);
  const condition = getConditionOption(entry.conditionStatus);
  const documentaryBonus =
    (entry.hasAuthenticityCertificate ? 5 : 0) +
    (entry.hasArtistProvenance ? 2 : 0) +
    (entry.hasImpeccableFollowUp ? 5 : 0);

  if (!referenceValue) {
    return {
      referenceValue: 0,
      adjustedValue: 0,
      conditionAdjustment: condition.adjustment,
      documentaryBonus,
      totalAdjustment: condition.adjustment + documentaryBonus,
      isLoss: condition.key === "irreparable",
      detail: "Valeur de référence non renseignée.",
    };
  }

  if (condition.key === "irreparable") {
    return {
      referenceValue,
      adjustedValue: 0,
      conditionAdjustment: -100,
      documentaryBonus: 0,
      totalAdjustment: -100,
      isLoss: true,
      detail: `État irréparable : valeur ramenée à 0 €, sortie du total actif. Valeur de référence : ${formatEuro(referenceValue)}.`,
    };
  }

  const totalAdjustment = condition.adjustment + documentaryBonus;
  const adjustedValue = Math.max(0, Math.round(referenceValue * (1 + totalAdjustment / 100)));
  return {
    referenceValue,
    adjustedValue,
    conditionAdjustment: condition.adjustment,
    documentaryBonus,
    totalAdjustment,
    isLoss: false,
    detail: `Base ${formatEuro(referenceValue)}. État : ${condition.label} (${condition.adjustment}%). Bonus documentaires : +${documentaryBonus}%. Valeur ajustée : ${formatEuro(adjustedValue)}.`,
  };
}

function capHighWithGallery(high, galleryCap) {
  const highNumber = toNumber(high);
  const cap = toNumber(galleryCap);
  if (highNumber === null) return cap || null;
  if (!cap) return highNumber;
  return Math.min(highNumber, cap);
}

function safeWeightedAverage(parts = []) {
  const usable = parts
    .map((part) => ({ value: toNumber(part.value), weight: toNumber(part.weight) || 0 }))
    .filter((part) => part.value !== null && part.value > 0 && part.weight > 0);

  const weightTotal = usable.reduce((sum, part) => sum + part.weight, 0);
  if (!usable.length || !weightTotal) return null;

  return usable.reduce((sum, part) => sum + part.value * part.weight, 0) / weightTotal;
}

function computeMethodologyValuationFromDraft({ methodGroup, draft = {}, entry = {} }) {
  const galleryCap = toNumber(draft.galleryPriceCap ?? entry.galleryPriceCap);
  const manualLow = toNumber(draft.priceLow ?? entry.valeurBasse);
  const manualHigh = toNumber(draft.priceHigh ?? entry.valeurHaute);
  const manualAverage = toNumber(draft.averageValue ?? entry.valeurRetenue);

  const ownRecent = toNumber(draft.ownResultsRecentValue ?? entry.ownResultsRecentValue);
  const own3to5 = toNumber(draft.ownResults3to5Value ?? entry.ownResults3to5Value);
  const comparableRecent = toNumber(draft.comparableResultsRecentValue ?? entry.comparableResultsRecentValue);
  const comparable3to5 = toNumber(draft.comparableResults3to5Value ?? entry.comparableResults3to5Value);

  let median = null;
  let low = manualLow;
  let high = manualHigh;
  let prudenceDiscountPct = 0;
  let valuationMode = methodGroup;

  if (methodGroup === "direct_cup") {
    median = safeWeightedAverage([
      { value: ownRecent, weight: VALUATION_RULES.direct_cup.ownRecentWeight },
      { value: own3to5, weight: VALUATION_RULES.direct_cup.own3to5Weight },
    ]) ?? manualAverage;

    if (median !== null) {
      low = low ?? Math.round(median * 0.85);
      high = high ?? Math.round(median * 1.15);
    }
  } else if (methodGroup === "analogy_cup") {
    const hasOwnResults = Boolean(ownRecent || own3to5);

    if (hasOwnResults) {
      valuationMode = "analogy_cup_with_own";
      median = safeWeightedAverage([
        { value: ownRecent, weight: VALUATION_RULES.analogy_cup_with_own.ownRecentWeight },
        { value: own3to5, weight: VALUATION_RULES.analogy_cup_with_own.own3to5Weight },
        { value: comparableRecent, weight: VALUATION_RULES.analogy_cup_with_own.comparableRecentWeight },
        { value: comparable3to5, weight: VALUATION_RULES.analogy_cup_with_own.comparable3to5Weight },
      ]) ?? manualAverage;
    } else {
      valuationMode = "analogy_cup_without_own";
      prudenceDiscountPct = toNumber(draft.prudenceDiscountPct ?? entry.prudenceDiscountPct) ?? VALUATION_RULES.analogy_cup_without_own.prudenceDiscountPct;
      const rawMedian = safeWeightedAverage([
        { value: comparableRecent, weight: VALUATION_RULES.analogy_cup_without_own.comparableRecentWeight },
        { value: comparable3to5, weight: VALUATION_RULES.analogy_cup_without_own.comparable3to5Weight },
      ]) ?? manualAverage;
      median = rawMedian !== null ? Math.round(rawMedian * (1 - prudenceDiscountPct / 100)) : null;
    }

    if (median !== null) {
      low = low ?? Math.round(median * 0.80);
      high = high ?? Math.round(median * 1.20);
    }
  } else if (methodGroup === "point_method") {
    const h = getEntryHeight(entry);
    const w = getEntryWidth(entry);
    const pointLow = toNumber(draft.pointLow ?? entry.pointLow);
    const pointHigh = toNumber(draft.pointHigh ?? entry.pointHigh);

    if (h && w && pointLow && pointHigh) {
      low = Math.round((h + w) * pointLow);
      high = Math.round((h + w) * pointHigh);
      median = Math.round((low + high) / 2);
    } else {
      median = manualAverage;
    }
  } else {
    median = manualAverage;
  }

  if (median === null && low && high) median = Math.round((low + high) / 2);
  if (median !== null && low === null) low = Math.round(median * 0.85);
  if (median !== null && high === null) high = Math.round(median * 1.15);

  const cappedHigh = capHighWithGallery(high, galleryCap);
  if (cappedHigh !== null && median !== null && cappedHigh < median) {
    median = cappedHigh;
  }
  if (low !== null && cappedHigh !== null && low > cappedHigh) {
    low = cappedHigh;
  }

  return {
    low: low !== null ? Math.round(low) : null,
    high: cappedHigh !== null ? Math.round(cappedHigh) : null,
    retained: median !== null ? Math.round(median) : null,
    galleryCap: galleryCap || null,
    galleryCapApplied: Boolean(galleryCap && high && cappedHigh !== high),
    prudenceDiscountPct,
    valuationMode,
  };
}

function countReferences(draft = {}, entry = {}) {
  const ownRecent = toNumber(draft.ownResultsRecentCount ?? entry.ownResultsRecentCount) || 0;
  const own3to5 = toNumber(draft.ownResults3to5Count ?? entry.ownResults3to5Count) || 0;
  const comparableRecent = toNumber(draft.comparableResultsRecentCount ?? entry.comparableResultsRecentCount) || 0;
  const comparable3to5 = toNumber(draft.comparableResults3to5Count ?? entry.comparableResults3to5Count) || 0;
  return {
    ownRecent,
    own3to5,
    comparableRecent,
    comparable3to5,
    ownTotal: ownRecent + own3to5,
    comparableTotal: comparableRecent + comparable3to5,
    total: ownRecent + own3to5 + comparableRecent + comparable3to5,
  };
}

function buildMethodologyJustification({ methodGroup, draft = {}, entry = {}, config = {} }) {
  const counts = countReferences(draft, entry);
  const valuation = computeMethodologyValuationFromDraft({ methodGroup, draft, entry });
  const galleryCap = toNumber(draft.galleryPriceCap ?? entry.galleryPriceCap);
  const detail = draft.referenceDetail || entry.referenceDetail || entry.groupBReferenceDetail || "";

  const freshnessWarning = "Les résultats de plus de 5 ans sont exclus de la moyenne, sauf absence totale d’alternative, auquel cas ils doivent être mentionnés séparément avec prudence.";
  const galleryText = galleryCap
    ? `Prix galerie actuel renseigné : ${formatEuro(galleryCap)}. Il sert uniquement de borne haute et ne rentre pas dans la moyenne pondérée.`
    : "Prix galerie non renseigné. S’il existe, il doit seulement plafonner la fourchette haute.";

  if (methodGroup === "direct_cup") {
    const minOk = counts.ownTotal >= VALUATION_RULES.direct_cup.minReferences;
    return [
      "Groupe A — CUP directe.",
      `Pondération validée : résultats enchères propres < 3 ans 60%, résultats propres 3-5 ans 30%, résultats > 5 ans exclus.`,
      `Base documentée : ${counts.ownTotal} résultat${counts.ownTotal > 1 ? "s" : ""} propre${counts.ownTotal > 1 ? "s" : ""} (${counts.ownRecent} < 3 ans, ${counts.own3to5} entre 3 et 5 ans).`,
      minOk ? "Seuil minimal CUP directe atteint." : `Attention : viser au moins ${VALUATION_RULES.direct_cup.minReferences} références propres pour une CUP directe robuste.`,
      galleryText,
      valuation.galleryCapApplied ? "Borne haute plafonnée par le prix galerie actuel." : null,
      detail ? `Sources / comparables : ${detail}` : null,
      freshnessWarning,
    ].filter(Boolean).join(" ");
  }

  if (methodGroup === "analogy_cup") {
    const hasOwn = counts.ownTotal > 0;
    const minOk = counts.total >= VALUATION_RULES.analogy_cup_with_own.minReferences;
    const analogText = config.analogArtists || entry.analogArtists || draft.analogArtists
      ? `Analogie : ${config.analogArtists || entry.analogArtists || draft.analogArtists}.`
      : "Analogie : artistes comparables à renseigner.";

    if (hasOwn) {
      return [
        "Groupe B — CUP par analogie avec résultats propres.",
        "Pondération validée : résultats propres < 3 ans 40%, résultats propres 3-5 ans 20%, comparables < 3 ans 30%, comparables 3-5 ans 10%, résultats > 5 ans exclus.",
        `Base documentée : ${counts.ownTotal} résultat${counts.ownTotal > 1 ? "s" : ""} propre${counts.ownTotal > 1 ? "s" : ""} (${counts.ownRecent} < 3 ans, ${counts.own3to5} entre 3 et 5 ans) et ${counts.comparableTotal} comparable${counts.comparableTotal > 1 ? "s" : ""} (${counts.comparableRecent} < 3 ans, ${counts.comparable3to5} entre 3 et 5 ans).`,
        minOk ? "Seuil minimal Groupe B atteint : au moins 5 références croisées." : `Attention : Groupe B recommandé avec minimum ${VALUATION_RULES.analogy_cup_with_own.minReferences} références croisées.`,
        analogText,
        config.analogyReason || entry.analogyReason ? `Logique d’analogie : ${config.analogyReason || entry.analogyReason}.` : null,
        galleryText,
        valuation.galleryCapApplied ? "Borne haute plafonnée par le prix galerie actuel." : null,
        detail ? `Sources / comparables : ${detail}` : null,
        freshnessWarning,
      ].filter(Boolean).join(" ");
    }

    return [
      "Groupe B — CUP par analogie exclusive.",
      "Aucun résultat propre vérifié — valorisation par analogie exclusive.",
      "Pondération validée : comparables < 3 ans 60%, comparables 3-5 ans 25%, résultats > 5 ans exclus.",
      `Base documentée : ${counts.comparableTotal} comparable${counts.comparableTotal > 1 ? "s" : ""} (${counts.comparableRecent} < 3 ans, ${counts.comparable3to5} entre 3 et 5 ans).`,
      minOk ? "Seuil minimal Groupe B atteint : au moins 5 références sur 2-3 artistes comparables." : `Attention : Groupe B sans résultat propre recommandé avec minimum ${VALUATION_RULES.analogy_cup_without_own.minReferences} références comparables.`,
      `Abattement prudence appliqué : -${valuation.prudenceDiscountPct || VALUATION_RULES.analogy_cup_without_own.prudenceDiscountPct}% — absence de résultats propres vérifiés.`,
      analogText,
      config.analogyReason || entry.analogyReason ? `Logique d’analogie : ${config.analogyReason || entry.analogyReason}.` : null,
      galleryText,
      valuation.galleryCapApplied ? "Borne haute plafonnée par le prix galerie actuel." : null,
      detail ? `Sources / comparables : ${detail}` : null,
      freshnessWarning,
    ].filter(Boolean).join(" ");
  }

  if (methodGroup === "point_method") {
    return [
      "Groupe C — Méthode à points.",
      "Pondération validée : formule (H + L) × valeur du point = 100% de la référence.",
      galleryText,
      valuation.galleryCapApplied ? "Borne haute plafonnée par le prix galerie actuel." : null,
      detail ? `Sources / justification : ${detail}` : null,
    ].filter(Boolean).join(" ");
  }

  return "Méthodologie de valorisation à compléter.";
}

function getFinalValue(entry = {}) {
  const override = toNumber(entry.expertOverrideValue);
  if (entry.expertOverrideEnabled && override !== null) return override;
  return computeConditionAdjustedValue(entry).adjustedValue;
}

function groupByArtist(entries = []) {
  return entries.reduce((acc, entry) => {
    const artistKey = entry.artistKey || normalizeArtistName(entry.artist);
    const artist = displayArtistName(entry.artist);
    if (!acc[artistKey]) {
      acc[artistKey] = { artistKey, artist, entries: [] };
    }
    acc[artistKey].entries.push({ ...entry, artistKey, artist: acc[artistKey].artist });
    return acc;
  }, {});
}

function buildBlockKey({ artistKey, mediumKey, sizeBucket }) {
  return [artistKey, mediumKey, normalizeText(sizeBucket)].join("|");
}

function groupEntriesByArtistMediumFormat(entries = []) {
  const groups = {};
  entries.forEach((entry) => {
    const artistKey = entry.artistKey || normalizeArtistName(entry.artist);
    const artist = displayArtistName(entry.artist);
    const medium = entry.mediumLabel || "Médium non renseigné";
    const mediumKey = entry.mediumKey || normalizeMediumKey(medium);
    const sizeBucket = entry.sizeBucket || "format non documenté";
    const key = buildBlockKey({ artistKey, mediumKey, sizeBucket });
    if (!groups[key]) groups[key] = { key, artistKey, artist, mediumKey, medium, sizeBucket, entries: [] };
    groups[key].entries.push({ ...entry, artistKey, artist: groups[key].artist, mediumKey });
  });
  return Object.values(groups).sort((a, b) => `${a.artist}${a.medium}${a.sizeBucket}`.localeCompare(`${b.artist}${b.medium}${b.sizeBucket}`));
}

function makeEntryFromArtwork(artwork = {}) {
  const h = getDimension(artwork, "height");
  const w = getDimension(artwork, "width");
  const existing =
    artwork.current_estimated_value ||
    artwork.valuation_consensus ||
    artwork.valuation_estimated_value ||
    artwork.estimated_value ||
    "";

  // Lire les données de valorisation expert déjà sauvegardées
  const cup = artwork.cup_comparables && typeof artwork.cup_comparables === "object"
    ? artwork.cup_comparables
    : {};

  // Groupe/méthode déjà établie — priorité à expert_method puis valuation_method puis cup
  const savedMethodGroup =
    artwork.expert_method ||
    artwork.valuation_method ||
    cup.method_group ||
    "direct_cup";

  // Fourchettes déjà sauvegardées — priorité à valuation_low/high puis price_low/high puis cup
  const savedLow = artwork.valuation_low ?? artwork.price_low ?? cup.reference_low ?? "";
  const savedHigh = artwork.valuation_high ?? artwork.price_high ?? cup.reference_high ?? "";

  // Données de pondération depuis cup_comparables.methodology
  const meth = cup.methodology && typeof cup.methodology === "object" ? cup.methodology : {};

  const mediumLabel = getMediumLabel(artwork);
  const artist = displayArtistName(artwork.artist);
  const artistKey = normalizeArtistName(artwork.artist);
  const mediumKey = normalizeMediumKey(mediumLabel);

  const hasExistingValuation = Boolean(savedLow || savedHigh || existing);

  return {
    artworkId: artwork.id,
    title: artwork.title || "Sans titre",
    artist,
    artistRaw: artwork.artist || "",
    artistKey,
    inventoryNumber: artwork.inventory_number || "",
    year: artwork.year || "",
    medium: artwork.medium || "",
    mediumLabel,
    mediumKey,
    typeOfArt: artwork.typeOfArt || "",
    height: h || "",
    width: w || "",
    depth: getDimension(artwork, "depth") || "",
    specifications: artwork.specifications || {},
    sizeBucket: getSizeBucket(h, w),
    valeurBasse: savedLow,
    valeurHaute: savedHigh,
    valeurRetenue: existing || "",
    existingValue: existing,
    methodGroup: savedMethodGroup,
    methodeUtilisee: VALUATION_METHODS[savedMethodGroup]?.methodKey || "semblables",
    sourcesConsultees: cup.sources || [],
    justification: meth.justification || artwork.expert_note || "",
    conditionStatus: cup.condition_status || normalizeConditionKey(artwork.condition || artwork.specifications?.condition),
    conditionNote: artwork.conditionNotes || "",
    expertOverrideEnabled: Boolean(cup.expert_override_enabled),
    expertOverrideValue: cup.expert_override_value || "",
    expertOverrideReason: cup.expert_override_reason || "",
    valuationConfidenceScore: artwork.valuation_confidence_score || null,
    hasArtkivEstimation: hasExistingValuation,
    hasExistingValuation,
    artkivValue: existing || null,
    lastEstimationDate: artwork.expert_date || artwork.last_estimation_date || artwork.last_valuation_date || null,
    isManual: false,
    // Données de pondération récupérées depuis cup_comparables.methodology
    ownResultsRecentCount: meth.own_results_recent_count ?? "",
    ownResults3to5Count: meth.own_results_3_to_5_count ?? "",
    ownResultsRecentValue: meth.own_results_recent_value ?? "",
    ownResults3to5Value: meth.own_results_3_to_5_value ?? "",
    comparableResultsRecentCount: meth.comparable_results_recent_count ?? "",
    comparableResults3to5Count: meth.comparable_results_3_to_5_count ?? "",
    comparableResultsRecentValue: meth.comparable_results_recent_value ?? "",
    comparableResults3to5Value: meth.comparable_results_3_to_5_value ?? "",
    galleryPriceCap: meth.gallery_price_cap ?? cup.gallery_price_cap ?? "",
    prudenceDiscountPct: meth.prudence_discount_pct ?? VALUATION_RULES.analogy_cup_without_own.prudenceDiscountPct,
    referenceDetail: meth.reference_detail ?? cup.reference_detail ?? "",
    analogArtists: cup.analog_artists || "",
    analogyReason: cup.analogy_reason || "",
    pointLow: cup.point_low || "",
    pointHigh: cup.point_high || "",
    ...inferDocFlags(artwork),
  };
}

function buildArtworkPayload(entry = {}) {
  const low = toNumber(entry.valeurBasse);
  const high = toNumber(entry.valeurHaute);
  const retained = toNumber(entry.valeurRetenue);
  const adjustment = computeConditionAdjustedValue(entry);
  const finalValue = getFinalValue(entry);
  const referenceValue = adjustment.referenceValue || retained || (low && high ? Math.round((low + high) / 2) : low || high || null);
  const method = VALUATION_METHODS[entry.methodGroup] || VALUATION_METHODS.direct_cup;
  const methodologyJustification = buildMethodologyJustification({
    methodGroup: entry.methodGroup || "direct_cup",
    entry,
    draft: entry,
    config: entry,
  });
  const noteParts = [
    `${method.group} — ${method.label}. ${method.description}`,
    methodologyJustification,
    entry.analogArtists ? `Artistes comparables : ${entry.analogArtists}` : null,
    entry.analogyReason ? `Logique d’analogie : ${entry.analogyReason}` : null,
    entry.justification,
    adjustment.detail,
    entry.expertOverrideEnabled && entry.expertOverrideReason ? `Exception experte : ${entry.expertOverrideReason}` : null,
  ].filter(Boolean);

  return {
    price_low: low,
    price_high: high,
    estimated_value: finalValue || null,
    current_estimated_value: finalValue || null,
    valuation_estimated_value: finalValue || null,
    valuation_market_value: referenceValue || null,
    valuation_consensus: finalValue || referenceValue || null,
    valuation_confidence_score: entry.valuationConfidenceScore || null,
    last_valuation_date: todayISO(),
    last_estimation_date: new Date().toISOString(),
    expert_note: noteParts.join("\n"),
    expert_date: todayISO(),
    expert_method: entry.methodGroup || "direct_cup",
    condition: entry.conditionStatus || null,
    conditionNotes: entry.conditionNote || null,
    certificate_auth: Boolean(entry.hasAuthenticityCertificate),
    certificate_blockchain: Boolean(entry.hasBlockchainCertificate),
    cup_comparables: {
      origin: "rapport_valorisation_advisor",
      artist: entry.artist,
      artist_key: entry.artistKey || normalizeArtistName(entry.artist),
      inventory_number: entry.inventoryNumber || null,
      method_group: entry.methodGroup,
      method_label: method.label,
      medium: entry.mediumLabel,
      medium_key: entry.mediumKey || normalizeMediumKey(entry.mediumLabel),
      size_bucket: entry.sizeBucket,
      reference_low: low,
      reference_high: high,
      reference_value: referenceValue,
      final_value: finalValue,
      sources: entry.sourcesConsultees || [],
      analog_artists: entry.analogArtists || null,
      analogy_reason: entry.analogyReason || null,
      point_low: entry.pointLow || null,
      point_high: entry.pointHigh || null,
      methodology: {
        own_results_recent_count: toNumber(entry.ownResultsRecentCount) || 0,
        own_results_3_to_5_count: toNumber(entry.ownResults3to5Count) || 0,
        own_results_recent_value: toNumber(entry.ownResultsRecentValue),
        own_results_3_to_5_value: toNumber(entry.ownResults3to5Value),
        comparable_results_recent_count: toNumber(entry.comparableResultsRecentCount) || 0,
        comparable_results_3_to_5_count: toNumber(entry.comparableResults3to5Count) || 0,
        comparable_results_recent_value: toNumber(entry.comparableResultsRecentValue),
        comparable_results_3_to_5_value: toNumber(entry.comparableResults3to5Value),
        gallery_price_cap: toNumber(entry.galleryPriceCap),
        prudence_discount_pct: toNumber(entry.prudenceDiscountPct),
        reference_detail: entry.referenceDetail || null,
        justification: methodologyJustification,
      },
      condition_status: entry.conditionStatus,
      condition_adjustment_pct: adjustment.conditionAdjustment,
      documentation_bonus_pct: adjustment.documentaryBonus,
      total_adjustment_pct: adjustment.totalAdjustment,
      expert_override_enabled: Boolean(entry.expertOverrideEnabled),
      expert_override_value: toNumber(entry.expertOverrideValue),
      expert_override_reason: entry.expertOverrideReason || null,
      saved_at: new Date().toISOString(),
    },
  };
}

// Cherche une cotation existante dans le cache pour un artwork donné
function findQuoteForArtwork(artwork, quotes) {
  if (!quotes || !quotes.length) return null;
  const artistKey = normalizeArtistName(artwork.artist);
  const mediumLabel = getMediumLabel(artwork);
  const h = artwork.height ?? artwork.dimensions_height;
  const w = artwork.width ?? artwork.dimensions_width;
  const sizeBucket = getSizeBucket(h, w);
  const mediumWithFormat = normalizeMediumKey(mediumLabel + " " + sizeBucket);
  const mediumSimple = normalizeMediumKey(mediumLabel);
  return quotes.find((q) => {
    const qArtist = q.artist_key || "";
    if (qArtist !== artistKey) return false;
    const qMedium = q.medium_key || "";
    return qMedium === mediumWithFormat || qMedium === mediumSimple || qMedium.includes(mediumSimple);
  }) || null;
}

export default function RapportValorisationAdvisor() {
  const { t } = useTranslation();
  const tr = (key, options) => t(`valuationReport.${key}`, options);
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [advisorName, setAdvisorName] = useState("");
  const [clientName, setClientName] = useState("");
  const [finalite, setFinalite] = useState("marche");

  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [selectedArtworkIds, setSelectedArtworkIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [collectionsMap, setCollectionsMap] = useState({});
  const [filterCollection, setFilterCollection] = useState("__all__");

  const [entries, setEntries] = useState([]);
  const [artistMethodologies, setArtistMethodologies] = useState({});
  const [blockDrafts, setBlockDrafts] = useState({});
  const [rapportNotes, setRapportNotes] = useState("");

  const [savingArtworks, setSavingArtworks] = useState(false);
  const [savingArtistQuotes, setSavingArtistQuotes] = useState(false);
  const [loadingArtistQuotes, setLoadingArtistQuotes] = useState(false);
  const [artistQuotePrefillCount, setArtistQuotePrefillCount] = useState(0);
  const [artistQuotesCache, setArtistQuotesCache] = useState([]); // cotations préchargées pour affichage step 2
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      if (!session) {
        navigate("/login");
        return;
      }

      setCurrentUserId(session.user.id);

      const { data: profile } = await supabase
        .from("profiles")
        .select("first_name,last_name,company,company_name")
        .eq("id", session.user.id)
        .single();

      if (!cancelled) {
        setAdvisorName(`${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() || session.user.email || "");
      }

      const builtSources = [];
      let allArtworks = [];

      const { data: ownArtworks } = await withoutProposalWorks(supabase
        .from("artworks")
        .select(ARTWORK_SELECT_FIELDS)
        .eq("client_id", session.user.id)
        .order("artist", { ascending: true }), session.user.id);

      if (ownArtworks?.length) {
        builtSources.push({ key: "__own__", label: "Mes œuvres", artworks: ownArtworks });
        allArtworks = [...ownArtworks];
      }

      const { data: perms } = await supabase
        .from("collection_permissions")
        .select("collection_id, client_id")
        .eq("grantee_id", session.user.id);

      const collectionIds = [...new Set((perms || []).map((p) => p.collection_id).filter(Boolean))];

      if (collectionIds.length) {
        let sharedArtworks = [];
        const { data: viewData, error: viewError } = await supabase
          .from("v_expert_artworks")
          .select(ARTWORK_SELECT_FIELDS)
          .in("collection_id", collectionIds)
          .order("artist", { ascending: true });

        if (!viewError && viewData?.length) {
          sharedArtworks = viewData;
        } else {
          const { data: tableData } = await supabase
            .from("artworks")
            .select(ARTWORK_SELECT_FIELDS)
            .in("collection_id", collectionIds)
            .order("artist", { ascending: true });
          sharedArtworks = tableData || [];
        }

        const collectionToClient = {};
        (perms || []).forEach((p) => {
          if (p.collection_id && p.client_id) collectionToClient[p.collection_id] = p.client_id;
        });

        sharedArtworks = sharedArtworks.map((a) => ({
          ...a,
          client_id: a.client_id || collectionToClient[a.collection_id] || "__unknown__",
        }));

        const byClient = {};
        sharedArtworks.forEach((a) => {
          const cid = a.client_id || "__unknown__";
          if (!byClient[cid]) byClient[cid] = [];
          byClient[cid].push(a);
        });

        const clientIds = Object.keys(byClient).filter((id) => id !== "__unknown__");
        let profilesMap = {};
        if (clientIds.length) {
          const { data: profiles } = await supabase
            .from("profiles")
            .select("id,first_name,last_name,company,company_name")
            .in("id", clientIds);
          (profiles || []).forEach((p) => {
            profilesMap[p.id] = `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.company_name || p.company || p.id;
          });
        }

        Object.entries(byClient).forEach(([clientId, aws]) => {
          builtSources.push({ key: clientId, label: profilesMap[clientId] || "Client partagé", artworks: aws });
          const existingIds = new Set(allArtworks.map((a) => a.id));
          allArtworks = [...allArtworks, ...aws.filter((a) => !existingIds.has(a.id))];
        });
      }

      const collectionMap = {};
      const colIds = [...new Set(allArtworks.map((a) => a.collection_id).filter(Boolean))];
      if (colIds.length) {
        const { data: cols } = await supabase.from("collections").select("id,title").in("id", colIds);
        (cols || []).forEach((c) => {
          collectionMap[c.id] = c.title || c.id;
        });
      }

      if (!cancelled) {
        setSources(builtSources);
        setArtworks(allArtworks);
        setCollectionsMap(collectionMap);
        if (builtSources[0]) setSelectedSource(builtSources[0].key);

        // Précharger toutes les cotations expert pour affichage step 2
        try {
          const { data: allQuotes } = await supabase
            .from("artist_quotes")
            .select("artist_key, medium_key, price_low, price_high, average_value")
            .eq("expert_id", session.user.id);
          if (!cancelled) setArtistQuotesCache(allQuotes || []);
        } catch (_) {}

        setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const sourceArtworks = useMemo(() => sources.find((s) => s.key === selectedSource)?.artworks || [], [sources, selectedSource]);

  const visibleArtworks = useMemo(() => {
    const q = normalizeText(searchQuery);
    return sourceArtworks.filter((artwork) => {
      if (filterCollection !== "__all__" && artwork.collection_id !== filterCollection) return false;
      if (!q) return true;
      return normalizeText(`${artwork.title || ""} ${artwork.artist || ""} ${artwork.inventory_number || ""}`).includes(q);
    });
  }, [sourceArtworks, searchQuery, filterCollection]);

  const selectedArtworks = useMemo(() => artworks.filter((a) => selectedArtworkIds.includes(a.id)), [artworks, selectedArtworkIds]);

  const artistGroups = useMemo(() => groupByArtist(entries), [entries]);
  const valuationBlocks = useMemo(() => groupEntriesByArtistMediumFormat(entries), [entries]);
  const activeValueEntries = useMemo(() => entries.filter((entry) => !computeConditionAdjustedValue(entry).isLoss), [entries]);
  const lossEntries = useMemo(() => entries.filter((entry) => computeConditionAdjustedValue(entry).isLoss), [entries]);
  const totalActive = useMemo(() => activeValueEntries.reduce((sum, entry) => sum + (getFinalValue(entry) || 0), 0), [activeValueEntries]);

  const updateEntry = (artworkId, patch) => {
    setEntries((prev) => prev.map((entry) => (entry.artworkId === artworkId ? { ...entry, ...patch } : entry)));
  };

  const updateArtistMethodology = (artistKey, patch) => {
    setArtistMethodologies((prev) => ({
      ...prev,
      [artistKey]: {
        methodGroup: "direct_cup",
        analogArtists: "",
        analogyReason: "",
        expertNote: "",
        ...(prev[artistKey] || {}),
        ...patch,
      },
    }));
  };

  const updateBlockDraft = (blockKey, patch) => {
    setBlockDrafts((prev) => ({
      ...prev,
      [blockKey]: {
        ...(prev[blockKey] || {}),
        ...patch,
      },
    }));
  };

  const buildQuoteMedium = (block) => `${block.medium} · ${block.sizeBucket}`;

  const loadPersonalArtistQuotesForEntries = async (nextEntries) => {
    if (!currentUserId || !nextEntries.length) {
      return { methods: {}, drafts: {}, count: 0 };
    }

    const artistKeys = [...new Set(nextEntries.map((entry) => entry.artistKey || normalizeArtistName(entry.artist)).filter(Boolean))];
    if (!artistKeys.length) return { methods: {}, drafts: {}, count: 0 };

    setLoadingArtistQuotes(true);

    try {
      const { data, error } = await supabase
        .from("artist_quotes")
        .select("*")
        .eq("expert_id", currentUserId)
        .in("artist_key", artistKeys)
        .order("updated_at", { ascending: false });

      if (error) throw error;

      const quotes = data || [];
      const blocks = groupEntriesByArtistMediumFormat(nextEntries);
      const methods = {};
      const drafts = {};
      let count = 0;

      const findQuoteForBlock = (block) => {
        const exactMedium = normalizeMediumKey(buildQuoteMedium(block));
        const simpleMedium = normalizeMediumKey(block.medium);
        return quotes.find((quote) => {
          const quoteArtistKey = quote.artist_key || normalizeArtistName(quote.artist);
          if (quoteArtistKey !== block.artistKey) return false;
          const quoteMedium = quote.medium_key || normalizeMediumKey(quote.medium);
          return quoteMedium === exactMedium || quoteMedium === simpleMedium || quoteMedium.includes(simpleMedium);
        });
      };

      blocks.forEach((block) => {
        const quote = findQuoteForBlock(block);
        if (!quote) return;

        count += 1;

        const methodGroup = quote.method_group || "direct_cup";
        methods[block.artistKey] = {
          methodGroup,
          analogArtists: Array.isArray(quote.analog_artists) ? quote.analog_artists.join(", ") : "",
          analogyReason: quote.analogy_reason || "",
          expertNote: quote.notes || "",
          quoteLoadedAt: quote.quote_date || quote.updated_at || "",
        };

        drafts[block.key] = {
          methodGroup,
          priceLow: quote.price_low ?? "",
          priceHigh: quote.price_high ?? "",
          averageValue: quote.average_value ?? "",
          galleryPriceCap: quote.gallery_price_cap ?? quote.gallery_cap_eur ?? "",
          ownResultsRecentCount: quote.own_results_recent_count ?? "",
          ownResults3to5Count: quote.own_results_3_to_5_count ?? "",
          ownResultsRecentValue: quote.own_results_recent_value ?? "",
          ownResults3to5Value: quote.own_results_3_to_5_value ?? "",
          comparableResultsRecentCount: quote.comparable_results_recent_count ?? "",
          comparableResults3to5Count: quote.comparable_results_3_to_5_count ?? "",
          comparableResultsRecentValue: quote.comparable_results_recent_value ?? "",
          comparableResults3to5Value: quote.comparable_results_3_to_5_value ?? "",
          prudenceDiscountPct: quote.prudence_discount_pct ?? VALUATION_RULES.analogy_cup_without_own.prudenceDiscountPct,
          referenceDetail: quote.reference_detail ?? "",
          pointPresetKey: inferPointPresetKey(block.medium),
          pointLow: quote.point_rate_low_eur ?? quote.point_rate_raw_eur ?? "",
          pointHigh: quote.point_rate_high_eur ?? quote.recommended_point_rate_eur ?? "",
          notes: quote.notes || "",
          confidence: quote.confidence || "med",
          quoteDate: quote.quote_date || quote.updated_at || "",
          loadedFromArtistQuote: true,
        };
      });

      return { methods, drafts, count };
    } catch (error) {
      console.error("loadPersonalArtistQuotesForEntries", error);
      setStatus({
        type: "error",
        message: error.message || "Impossible de précharger les cotations personnelles artiste/médium.",
      });
      return { methods: {}, drafts: {}, count: 0 };
    } finally {
      setLoadingArtistQuotes(false);
    }
  };

  const prepareArtistClassification = async () => {
    if (!selectedArtworks.length) return;

    setStatus(null);
    const nextEntries = selectedArtworks.map(makeEntryFromArtwork);
    const grouped = groupByArtist(nextEntries);
    const defaultMethods = {};

    // Pré-remplir le groupe depuis les données déjà sauvegardées sur les fiches
    Object.values(grouped).forEach(({ artistKey, entries: artistEntries }) => {
      // Chercher si au moins une fiche de cet artiste a déjà une méthode sauvegardée
      const representativeEntry = artistEntries.find((e) => e.methodGroup && e.methodGroup !== "direct_cup")
        || artistEntries[0];
      const savedMethod = representativeEntry?.methodGroup || "direct_cup";
      defaultMethods[artistKey] = {
        methodGroup: savedMethod,
        analogArtists: representativeEntry?.analogArtists || "",
        analogyReason: representativeEntry?.analogyReason || "",
        expertNote: "",
      };
    });

    const prefill = await loadPersonalArtistQuotesForEntries(nextEntries);

    setEntries(nextEntries);
    setArtistMethodologies({
      ...defaultMethods,
      ...prefill.methods,
    });

    // Construire des drafts depuis les données déjà sauvegardées sur les fiches
    // (les artist_quotes ont priorité et écrasent si elles existent)
    const artworkDrafts = {};
    const blocks = groupEntriesByArtistMediumFormat(nextEntries);
    blocks.forEach((block) => {
      const representativeEntry = block.entries.find((e) => e.hasExistingValuation) || block.entries[0];
      if (!representativeEntry?.hasExistingValuation) return;
      artworkDrafts[block.key] = {
        methodGroup: representativeEntry.methodGroup || "direct_cup",
        priceLow: representativeEntry.valeurBasse || "",
        priceHigh: representativeEntry.valeurHaute || "",
        averageValue: representativeEntry.valeurRetenue || "",
        galleryPriceCap: representativeEntry.galleryPriceCap || "",
        ownResultsRecentCount: representativeEntry.ownResultsRecentCount || "",
        ownResults3to5Count: representativeEntry.ownResults3to5Count || "",
        ownResultsRecentValue: representativeEntry.ownResultsRecentValue || "",
        ownResults3to5Value: representativeEntry.ownResults3to5Value || "",
        comparableResultsRecentCount: representativeEntry.comparableResultsRecentCount || "",
        comparableResults3to5Count: representativeEntry.comparableResults3to5Count || "",
        comparableResultsRecentValue: representativeEntry.comparableResultsRecentValue || "",
        comparableResults3to5Value: representativeEntry.comparableResults3to5Value || "",
        prudenceDiscountPct: representativeEntry.prudenceDiscountPct || VALUATION_RULES.analogy_cup_without_own.prudenceDiscountPct,
        referenceDetail: representativeEntry.referenceDetail || "",
        pointPresetKey: inferPointPresetKey(representativeEntry.mediumLabel || ""),
        pointLow: representativeEntry.pointLow || "",
        pointHigh: representativeEntry.pointHigh || "",
        loadedFromArtwork: true,
      };
    });

    setBlockDrafts({ ...artworkDrafts, ...prefill.drafts });
    setArtistQuotePrefillCount(prefill.count || 0);

    const alreadyValuated = nextEntries.filter((e) => e.hasExistingValuation).length;
    if (prefill.count > 0 || alreadyValuated > 0) {
      const parts = [];
      if (alreadyValuated > 0) parts.push(`${alreadyValuated} fiche${alreadyValuated > 1 ? "s" : ""} déjà estimée${alreadyValuated > 1 ? "s" : ""} — groupes, fourchettes et pondérations préchargés.`);
      if (prefill.count > 0) parts.push(`${prefill.count} cotation${prefill.count > 1 ? "s" : ""} personnelle${prefill.count > 1 ? "s" : ""} chargée${prefill.count > 1 ? "s" : ""} depuis votre bibliothèque.`);
      setStatus({ type: "success", message: parts.join(" ") });
    } else {
      setStatus({
        type: "info",
        message: "Aucune cotation personnelle existante trouvée pour ces artistes. Renseignez les groupes et fourchettes, puis sauvegardez-les uniquement si vous voulez les réutiliser.",
      });
    }

    setStep(3);
  };

  const applyArtistGroupsToEntries = () => {
    setEntries((prev) =>
      prev.map((entry) => {
        const artistKey = entry.artistKey || normalizeArtistName(entry.artist);
        const config = artistMethodologies[artistKey] || { methodGroup: "direct_cup" };
        const method = VALUATION_METHODS[config.methodGroup] || VALUATION_METHODS.direct_cup;
        return {
          ...entry,
          artistKey,
          methodGroup: config.methodGroup,
          methodeUtilisee: method.methodKey,
          analogArtists: config.analogArtists || "",
          analogyReason: config.analogyReason || "",
          methodExpertNote: config.expertNote || "",
        };
      })
    );
    setStep(4);
  };

  const getBlockDraft = (block) => {
    const draft = blockDrafts[block.key] || {};
    if (Object.keys(draft).length) return draft;

    const presetKey = inferPointPresetKey(block.medium);
    const preset = POINT_PRESETS[presetKey] || POINT_PRESETS.other;
    const values = block.entries.map((entry) => toNumber(entry.valeurRetenue || entry.existingValue)).filter((v) => v !== null && v > 0);
    const average = values.length ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length) : "";

    return {
      methodGroup: artistMethodologies[block.artistKey]?.methodGroup || "direct_cup",
      priceLow: block.entries[0]?.valeurBasse || "",
      priceHigh: block.entries[0]?.valeurHaute || "",
      averageValue: average,
      pointPresetKey: presetKey,
      pointLow: preset.low,
      pointHigh: preset.high,
      galleryPriceCap: "",
      ownResultsRecentCount: "",
      ownResults3to5Count: "",
      ownResultsRecentValue: "",
      ownResults3to5Value: "",
      comparableResultsRecentCount: "",
      comparableResults3to5Count: "",
      comparableResultsRecentValue: "",
      comparableResults3to5Value: "",
      prudenceDiscountPct: VALUATION_RULES.analogy_cup_without_own.prudenceDiscountPct,
      referenceDetail: "",
      notes: "",
      confidence: "med",
    };
  };

  const applyBlockToEntries = (block) => {
    const draft = getBlockDraft(block);
    const methodGroup = artistMethodologies[block.artistKey]?.methodGroup || draft.methodGroup || "direct_cup";
    const config = artistMethodologies[block.artistKey] || {};

    setEntries((prev) =>
      prev.map((entry) => {
        if (!block.entries.some((candidate) => candidate.artworkId === entry.artworkId)) return entry;

        const valuation = computeMethodologyValuationFromDraft({ methodGroup, draft, entry });
        const low = valuation.low;
        const high = valuation.high;
        const retained = valuation.retained;

        const method = VALUATION_METHODS[methodGroup] || VALUATION_METHODS.direct_cup;
        const baseJustification = buildMethodologyJustification({
          methodGroup,
          draft,
          entry,
          config,
        });

        return {
          ...entry,
          artistKey: block.artistKey,
          mediumKey: block.mediumKey,
          methodGroup,
          methodeUtilisee: method.methodKey,
          valeurBasse: low || "",
          valeurHaute: high || "",
          valeurRetenue: retained || "",
          analogArtists: config.analogArtists || "",
          analogyReason: config.analogyReason || "",
          pointPresetKey: draft.pointPresetKey,
          pointLow: draft.pointLow,
          pointHigh: draft.pointHigh,
          ownResultsRecentCount: draft.ownResultsRecentCount || "",
          ownResults3to5Count: draft.ownResults3to5Count || "",
          ownResultsRecentValue: draft.ownResultsRecentValue || "",
          ownResults3to5Value: draft.ownResults3to5Value || "",
          comparableResultsRecentCount: draft.comparableResultsRecentCount || "",
          comparableResults3to5Count: draft.comparableResults3to5Count || "",
          comparableResultsRecentValue: draft.comparableResultsRecentValue || "",
          comparableResults3to5Value: draft.comparableResults3to5Value || "",
          galleryPriceCap: draft.galleryPriceCap || "",
          prudenceDiscountPct: draft.prudenceDiscountPct || VALUATION_RULES.analogy_cup_without_own.prudenceDiscountPct,
          referenceDetail: draft.referenceDetail || "",
          valuationConfidenceScore: draft.confidence === "high" ? 85 : draft.confidence === "low" ? 45 : 65,
          justification: entry.justification || baseJustification,
        };
      })
    );
  };

  const buildArtistQuoteRows = () => {
    const rows = [];
    valuationBlocks.forEach((block) => {
      const draft = getBlockDraft(block);
      const methodGroup = artistMethodologies[block.artistKey]?.methodGroup || draft.methodGroup || "direct_cup";
      const config = artistMethodologies[block.artistKey] || {};
      const reference = block.entries[0] || {};
      const valuation = computeMethodologyValuationFromDraft({ methodGroup, draft, entry: reference });
      const low = valuation.low;
      const high = valuation.high;
      const average = valuation.retained;
      const pointLow = toNumber(draft.pointLow);
      const pointHigh = toNumber(draft.pointHigh);
      const h = getEntryHeight(reference);
      const w = getEntryWidth(reference);

      if (!average && methodGroup !== "point_method") return;
      if (methodGroup === "point_method" && (!pointLow || !pointHigh)) return;

      const pointAverage = pointLow && pointHigh ? (pointLow + pointHigh) / 2 : null;
      rows.push({
        expert_id: currentUserId,
        artist: block.artist,
        artist_key: block.artistKey,
        medium: buildQuoteMedium(block),
        medium_key: normalizeMediumKey(buildQuoteMedium(block)),
        average_value: average || (h && w && pointAverage ? Math.round((h + w) * pointAverage) : 0),
        price_low: low,
        price_high: high,
        quote_date: todayISO(),
        gallery_price_cap: toNumber(draft.galleryPriceCap),
        own_results_recent_count: toNumber(draft.ownResultsRecentCount) || 0,
        own_results_3_to_5_count: toNumber(draft.ownResults3to5Count) || 0,
        own_results_recent_value: toNumber(draft.ownResultsRecentValue),
        own_results_3_to_5_value: toNumber(draft.ownResults3to5Value),
        comparable_results_recent_count: toNumber(draft.comparableResultsRecentCount) || 0,
        comparable_results_3_to_5_count: toNumber(draft.comparableResults3to5Count) || 0,
        comparable_results_recent_value: toNumber(draft.comparableResultsRecentValue),
        comparable_results_3_to_5_value: toNumber(draft.comparableResults3to5Value),
        prudence_discount_pct: toNumber(draft.prudenceDiscountPct) || VALUATION_RULES.analogy_cup_without_own.prudenceDiscountPct,
        reference_detail: draft.referenceDetail || null,
        method_group: methodGroup,
        analog_artists: config.analogArtists ? String(config.analogArtists).split(",").map((v) => v.trim()).filter(Boolean) : null,
        analogy_reason: config.analogyReason || null,
        notes: [config.expertNote, draft.notes].filter(Boolean).join("\n") || null,
        reference_h_cm: h || null,
        reference_w_cm: w || null,
        point_rate_low_eur: pointLow,
        point_rate_high_eur: pointHigh,
        recommended_point_rate_eur: pointAverage,
        confidence: draft.confidence || "med",
        updated_at: new Date().toISOString(),
      });
    });
    return rows.filter((row) => row.artist_key && row.medium_key);
  };

  const saveArtistQuotes = async () => {
    const rows = buildArtistQuoteRows();
    if (!currentUserId) {
      setStatus({ type: "error", message: "Session expert introuvable." });
      return;
    }
    if (!rows.length) {
      setStatus({ type: "error", message: "Aucun profil artiste/médium exploitable à sauvegarder." });
      return;
    }

    setSavingArtistQuotes(true);
    setStatus(null);
    try {
      const { error } = await supabase.from("artist_quotes").upsert(rows, {
        onConflict: "expert_id,artist_key,medium_key",
      });
      if (error) throw error;
      setStatus({ type: "success", message: `${rows.length} cotation${rows.length > 1 ? "s" : ""} artiste/médium sauvegardée${rows.length > 1 ? "s" : ""}.` });
    } catch (error) {
      console.error("saveArtistQuotes", error);
      setStatus({ type: "error", message: error.message || "Erreur de sauvegarde des cotations artistes." });
    } finally {
      setSavingArtistQuotes(false);
    }
  };

  const saveArtworks = async () => {
    const rows = entries.filter((entry) => !entry.isManual && entry.artworkId);
    if (!rows.length) {
      setStatus({ type: "error", message: "Aucune fiche œuvre à sauvegarder." });
      return;
    }

    setSavingArtworks(true);
    setStatus(null);
    try {
      const updates = rows.map((entry) => supabase.from("artworks").update(buildArtworkPayload(entry)).eq("id", entry.artworkId));
      const results = await Promise.all(updates);
      const failed = results.find((res) => res.error);
      if (failed?.error) throw failed.error;
      setStatus({ type: "success", message: `${rows.length} fiche${rows.length > 1 ? "s" : ""} œuvre sauvegardée${rows.length > 1 ? "s" : ""} dans artworks.` });
    } catch (error) {
      console.error("saveArtworks", error);
      setStatus({ type: "error", message: error.message || "Erreur de sauvegarde dans artworks." });
    } finally {
      setSavingArtworks(false);
    }
  };

  const exportPDF = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    let y = 18;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Rapport de valorisation ARTKIV", 20, y);
    y += 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Art Advisor : ${advisorName || "—"}`, 20, y);
    y += 6;
    doc.text(`Client : ${clientName || "—"}`, 20, y);
    y += 6;
    doc.text(`Finalité : ${FINALITES.find((f) => f.key === finalite)?.label || "—"}`, 20, y);
    y += 6;
    doc.text(`Valorisation établie le ${new Date().toLocaleDateString("fr-FR")}`, 20, y);
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.text(`Total actif ajusté : ${formatEuro(totalActive)}`, 20, y);
    y += 10;

    entries.forEach((entry, index) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.setFont("helvetica", "bold");
      doc.text(`${index + 1}. ${entry.title}`, 20, y);
      y += 5;
      doc.setFont("helvetica", "normal");
      doc.text(`${entry.artist} ${entry.inventoryNumber ? `· Inv. ${entry.inventoryNumber}` : ""}`, 20, y);
      y += 5;
      doc.text(`Fourchette : ${formatEuro(entry.valeurBasse)} – ${formatEuro(entry.valeurHaute)} · Valeur finale : ${formatEuro(getFinalValue(entry))}`, 20, y);
      y += 6;

      const methodologyText = buildMethodologyJustification({
        methodGroup: entry.methodGroup || "direct_cup",
        entry,
        draft: entry,
        config: entry,
      });
      doc.setFont("helvetica", "bold");
      doc.text("Lecture expert du marché", 20, y);
      y += 5;
      doc.setFont("helvetica", "normal");
      const wrappedMethodology = doc.splitTextToSize(methodologyText, 170);
      wrappedMethodology.slice(0, 6).forEach((line) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        doc.text(line, 20, y);
        y += 4.5;
      });
      y += 3;
    });

    // Mention de prudence (rapport Artprice 2026 : le dernier prix n'est pas la valeur)
    const note = doc.splitTextToSize(valuationFooter([], "fr"), 170);
    if (y + note.length * 4.5 > 280) {
      doc.addPage();
      y = 20;
    }
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.text(note, 20, y + 4);

    doc.save(`rapport_valorisation_${Date.now()}.pdf`);
  };

  const exportWord = async () => {
    const content = [
      "Rapport de valorisation ARTKIV",
      `Art Advisor : ${advisorName || "—"}`,
      `Client : ${clientName || "—"}`,
      `Total actif ajusté : ${formatEuro(totalActive)}`,
      `Valorisation établie le ${new Date().toLocaleDateString("fr-FR")}`,
      "",
      ...entries.map((entry, index) => {
        const methodologyText = buildMethodologyJustification({
          methodGroup: entry.methodGroup || "direct_cup",
          entry,
          draft: entry,
          config: entry,
        });
        return `${index + 1}. ${entry.title} — ${entry.artist} — ${formatEuro(getFinalValue(entry))}\nLecture expert du marché : ${methodologyText}`;
      }),
      "",
      valuationFooter([], "fr"),
    ].join("\n");
    const blob = new Blob([content], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rapport_valorisation_${Date.now()}.doc`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSource = (artworkId, sourceKey) => {
    setEntries((prev) =>
      prev.map((entry) => {
        if (entry.artworkId !== artworkId) return entry;
        const sources = entry.sourcesConsultees || [];
        return {
          ...entry,
          sourcesConsultees: sources.includes(sourceKey) ? sources.filter((s) => s !== sourceKey) : [...sources, sourceKey],
        };
      })
    );
  };

  if (loading) {
    return <div className="max-w-5xl mx-auto px-6 py-12 text-sm text-gray-500">{tr("loading")}</div>;
  }

  const selectedCollectionIds = [...new Set(sourceArtworks.map((a) => a.collection_id).filter(Boolean))];
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">{tr("kicker")}</p>
          <h1 className="text-4xl font-serif mb-2">{tr("title")}</h1>
          <p className="text-sm text-gray-500 max-w-2xl">{tr("description")}</p>

          {/* Bandeau rapport inflation */}
          <div className="mt-6 flex items-center justify-between gap-4 border border-green-200 bg-green-50 px-4 py-3 rounded">
            <div className="flex items-center gap-3">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M2 12L6 7L9 10L13 4" stroke="#1e6432" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div>
                <span className="text-sm font-medium text-green-900">{tr("inflation.title")}</span>
                <span className="ml-2 text-xs text-green-700">— {tr("inflation.description")}</span>
              </div>
            </div>
            <Link to="/inflation-report" className="flex-shrink-0 text-xs font-medium text-green-800 border border-green-300 bg-white px-3 py-1.5 rounded hover:bg-green-100 transition-colors" style={{ textDecoration: "none" }}>
              {tr("actions.open")} →
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-4 gap-2 mb-8 text-xs">
          {[tr("steps.purpose"), tr("steps.artworks"), tr("steps.artists"), tr("steps.records")].map((label, index) => (
            <div key={label} className={`border px-3 py-2 ${step === index + 1 ? "bg-black text-white border-black" : "bg-white text-gray-500 border-gray-200"}`}>{index + 1}. {label}</div>
          ))}
        </div>

        {status && (
          <div
            className={`mb-6 border-l-4 p-4 text-sm ${
              status.type === "success"
                ? "bg-green-50 border-green-500 text-green-800"
                : status.type === "info"
                  ? "bg-blue-50 border-blue-500 text-blue-800"
                  : "bg-red-50 border-red-500 text-red-800"
            }`}
          >
            {status.message}
          </div>
        )}

        {step === 1 && (
          <section className="bg-white border border-gray-200 p-6">
            <h2 className="text-2xl font-serif mb-4">{tr("purpose.title")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              {FINALITES.map((item) => (
                <button key={item.key} type="button" onClick={() => setFinalite(item.key)} className={`text-left border p-4 ${finalite === item.key ? "bg-black text-white border-black" : "bg-white hover:border-black"}`}>
                  <p className="font-medium">{tr(`purpose.options.${item.key}.label`)}</p>
                  <p className="text-xs mt-2 opacity-75">{tr(`purpose.options.${item.key}.description`)}</p>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div>
                <label className="block text-xs text-gray-500 mb-1">{tr("fields.clientName")}</label>
                <input value={clientName} onChange={(e) => setClientName(e.target.value)} className="w-full border border-gray-200 px-3 py-2" placeholder={tr("fields.clientPlaceholder")} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Art Advisor</label>
                <input value={advisorName} onChange={(e) => setAdvisorName(e.target.value)} className="w-full border border-gray-200 px-3 py-2" />
              </div>
            </div>
            <button type="button" onClick={() => setStep(2)} className="bg-black text-white px-8 py-3 text-sm">{tr("actions.chooseArtworks")}</button>
          </section>
        )}

        {step === 2 && (
          <section className="bg-white border border-gray-200 p-6">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h2 className="text-2xl font-serif">{tr("selection.title")}</h2>
                <p className="text-sm text-gray-500 mt-1">{tr("selection.description")}</p>
              </div>
              <div className="text-right text-sm">
                <p className="font-medium">{tr("selection.selected", { count: selectedArtworkIds.length })}</p>
              </div>
            </div>

            <div className="mb-5 border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
              <p className="font-medium">{tr("selection.phaseTitle")}</p>
              <p className="mt-1 text-xs leading-relaxed">
                {tr("selection.phaseDescription")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <select value={selectedSource || ""} onChange={(e) => { setSelectedSource(e.target.value); setSelectedArtworkIds([]); }} className="border border-gray-200 px-3 py-2">
                {sources.map((source) => <option key={source.key} value={source.key}>{source.label} ({source.artworks.length})</option>)}
              </select>
              <select value={filterCollection} onChange={(e) => setFilterCollection(e.target.value)} className="border border-gray-200 px-3 py-2">
                <option value="__all__">{tr("selection.allCollections")}</option>
                {selectedCollectionIds.map((id) => <option key={id} value={id}>{collectionsMap[id] || id}</option>)}
              </select>
              <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="border border-gray-200 px-3 py-2" placeholder={tr("selection.search")} />
            </div>

            <div className="flex items-center justify-between mb-3 text-sm">
              <button type="button" onClick={() => {
                const ids = visibleArtworks.map((a) => a.id);
                const allSelected = ids.every((id) => selectedArtworkIds.includes(id));
                setSelectedArtworkIds((prev) => allSelected ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])]);
              }} className="border border-gray-300 px-3 py-1 hover:border-black">
                {tr("selection.toggleAll", { count: visibleArtworks.length })}
              </button>
            </div>

            <div className="border border-gray-200 divide-y divide-gray-100 max-h-[520px] overflow-auto">
              {visibleArtworks.map((artwork) => (
                <label key={artwork.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={selectedArtworkIds.includes(artwork.id)} onChange={() => setSelectedArtworkIds((prev) => prev.includes(artwork.id) ? prev.filter((id) => id !== artwork.id) : [...prev, artwork.id])} className="w-4 h-4 accent-black" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{artwork.title || tr("common.untitled")}</p>
                    <p className="text-xs text-gray-500">{displayArtistName(artwork.artist)} · {getMediumLabel(artwork)} · {getSizeBucket(getDimension(artwork, "height"), getDimension(artwork, "width"))}{artwork.inventory_number ? ` · Inv. ${artwork.inventory_number}` : ""}</p>
                  </div>
                  <span className="text-xs text-right">
                    {(() => {
                      if (artwork.price_low || artwork.price_high) {
                        return <span className="text-gray-500">{formatEuro(artwork.price_low)} – {formatEuro(artwork.price_high)}</span>;
                      }
                      const quote = findQuoteForArtwork(artwork, artistQuotesCache);
                      if (quote && (quote.price_low || quote.price_high)) {
                        return (
                          <span className="text-green-700 font-medium">
                            {formatEuro(quote.price_low)} – {formatEuro(quote.price_high)}
                            <span className="block text-[10px] text-green-600 font-normal">{tr("selection.savedQuote")}</span>
                          </span>
                        );
                      }
                      return <span className="text-gray-300">{tr("selection.noRange")}</span>;
                    })()}
                  </span>
                </label>
              ))}
            </div>

            <div className="mt-8 flex justify-between">
              <button type="button" onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-black">{tr("actions.back")}</button>
              <button
                type="button"
                onClick={prepareArtistClassification}
                disabled={!selectedArtworkIds.length || loadingArtistQuotes}
                className="bg-black text-white px-8 py-3 text-sm disabled:opacity-40"
              >
                {loadingArtistQuotes ? tr("selection.loadingQuotes") : tr("selection.classify", { count: selectedArtworkIds.length })}
              </button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="space-y-6">
            <div className="bg-white border border-gray-200 p-6">
              <h2 className="text-2xl font-serif mb-1">{tr("classification.title")}</h2>
              <p className="text-sm text-gray-500">{tr("classification.description")}</p>
              <div className="mt-4 border border-gray-100 bg-gray-50 p-3 text-xs text-gray-600">
                {artistQuotePrefillCount > 0
                  ? tr("classification.prefilled", { count: artistQuotePrefillCount })
                  : tr("classification.none")}
              </div>
            </div>

            {Object.values(artistGroups).map(({ artistKey, artist, entries: artistEntries }) => {
              const config = artistMethodologies[artistKey] || { methodGroup: "direct_cup" };
              return (
                <div key={artistKey} className="bg-white border border-gray-200 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-[170px_1fr] gap-5">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gray-400 mb-1">{tr("fields.artist")}</p>
                      <h3 className="text-xl font-serif break-words">{artist}</h3>
                      <p className="text-xs text-gray-500 mt-2">{tr("common.artworkCount", { count: artistEntries.length })}</p>
                    </div>
                    <div>
                      <select value={config.methodGroup || "direct_cup"} onChange={(e) => updateArtistMethodology(artistKey, { methodGroup: e.target.value })} className="w-full border border-gray-200 px-3 py-2 mb-4">
                        {Object.entries(VALUATION_METHODS).map(([key, method]) => <option key={key} value={key}>{method.group} — {method.label}</option>)}
                      </select>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        {Object.entries(VALUATION_METHODS).map(([key, method]) => (
                          <button key={key} type="button" onClick={() => updateArtistMethodology(artistKey, { methodGroup: key })} className={`text-left border p-4 ${config.methodGroup === key ? "bg-black text-white border-black" : "bg-white hover:border-black"}`}>
                            <p className="text-sm font-medium">{method.group} — {method.label}</p>
                            <p className="text-xs mt-2 opacity-75">{method.short}</p>
                          </button>
                        ))}
                      </div>
                      {config.methodGroup === "analogy_cup" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                          <input value={config.analogArtists || ""} onChange={(e) => updateArtistMethodology(artistKey, { analogArtists: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("classification.comparableArtists")} />
                          <input value={config.analogyReason || ""} onChange={(e) => updateArtistMethodology(artistKey, { analogyReason: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("classification.analogyReason")} />
                        </div>
                      )}
                      <textarea value={config.expertNote || ""} onChange={(e) => updateArtistMethodology(artistKey, { expertNote: e.target.value })} rows={2} className="w-full border border-gray-200 px-3 py-2 text-sm" placeholder={tr("classification.expertNote")} />
                      <div className="mt-4 border border-gray-100 divide-y divide-gray-100">
                        {artistEntries.slice(0, 8).map((entry) => <div key={entry.artworkId} className="flex justify-between px-3 py-2 text-xs"><span>{entry.title}{entry.inventoryNumber ? ` · Inv. ${entry.inventoryNumber}` : ""}</span><span className="text-gray-400">{entry.mediumLabel} · {entry.sizeBucket}</span></div>)}
                        {artistEntries.length > 8 && <div className="px-3 py-2 text-xs text-gray-400">+ {tr("classification.otherArtworks", { count: artistEntries.length - 8 })}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="flex justify-between">
              <button type="button" onClick={() => setStep(2)} className="text-sm text-gray-500 hover:text-black">{tr("actions.back")}</button>
              <button type="button" onClick={applyArtistGroupsToEntries} className="bg-black text-white px-8 py-3 text-sm">{tr("actions.defineRanges")}</button>
            </div>
          </section>
        )}

        {step === 4 && (
          <section className="space-y-8">
            <div className="bg-white border border-gray-200 p-6">
              <h2 className="text-2xl font-serif mb-1">{tr("ranges.title")}</h2>
              <p className="text-sm text-gray-500">{tr("ranges.description")}</p>
            </div>

            <div className="space-y-5">
              {valuationBlocks.map((block) => {
                const draft = getBlockDraft(block);
                const artistConfig = artistMethodologies[block.artistKey] || { methodGroup: "direct_cup" };
                const methodGroup = artistConfig.methodGroup || "direct_cup";
                const method = VALUATION_METHODS[methodGroup] || VALUATION_METHODS.direct_cup;
                return (
                  <div key={block.key} className="bg-white border border-gray-200 p-5">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                      <div>
                        <p className="text-xs uppercase tracking-widest text-gray-400">{method.group} — {method.label}</p>
                        <h3 className="text-lg font-serif">{block.artist}</h3>
                        <p className="text-sm text-gray-500">{block.medium} · {block.sizeBucket} · {block.entries.length} œuvre{block.entries.length > 1 ? "s" : ""}</p>
                        {draft.loadedFromArtistQuote && (
                          <p className="text-xs text-green-700 mt-1">
                            {tr("ranges.prefilled")}{draft.quoteDate ? ` · ${tr("common.date")}: ${String(draft.quoteDate).slice(0, 10)}` : ""}
                          </p>
                        )}
                        {draft.loadedFromArtwork && !draft.loadedFromArtistQuote && (
                          <p className="text-xs text-amber-700 mt-1">
                            {tr("ranges.artworkPrefilled")}
                          </p>
                        )}
                      </div>
                      <button type="button" onClick={() => applyBlockToEntries(block)} className="bg-black text-white px-5 py-2 text-sm">{tr("ranges.applyToRecords", { count: block.entries.length })}</button>
                    </div>

                    {methodGroup === "point_method" ? (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                        <select value={draft.pointPresetKey || "other"} onChange={(e) => {
                          const preset = POINT_PRESETS[e.target.value] || POINT_PRESETS.other;
                          updateBlockDraft(block.key, { pointPresetKey: e.target.value, pointLow: preset.low, pointHigh: preset.high });
                        }} className="border border-gray-200 px-3 py-2">
                          {Object.entries(POINT_PRESETS).map(([key, preset]) => <option key={key} value={key}>{preset.label}</option>)}
                        </select>
                        <input type="number" value={draft.pointLow || ""} onChange={(e) => updateBlockDraft(block.key, { pointLow: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.pointLow")} />
                        <input type="number" value={draft.pointHigh || ""} onChange={(e) => updateBlockDraft(block.key, { pointHigh: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.pointHigh")} />
                        <select value={draft.confidence || "med"} onChange={(e) => updateBlockDraft(block.key, { confidence: e.target.value })} className="border border-gray-200 px-3 py-2">
                          <option value="low">{tr("confidence.low")}</option>
                          <option value="med">{tr("confidence.medium")}</option>
                          <option value="high">{tr("confidence.high")}</option>
                        </select>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                        <input type="number" value={draft.priceLow || ""} onChange={(e) => updateBlockDraft(block.key, { priceLow: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.low")} />
                        <input type="number" value={draft.priceHigh || ""} onChange={(e) => updateBlockDraft(block.key, { priceHigh: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.high")} />
                        <input type="number" value={draft.averageValue || ""} onChange={(e) => updateBlockDraft(block.key, { averageValue: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.average")} />
                        <select value={draft.confidence || "med"} onChange={(e) => updateBlockDraft(block.key, { confidence: e.target.value })} className="border border-gray-200 px-3 py-2">
                          <option value="low">{tr("confidence.low")}</option>
                          <option value="med">{tr("confidence.medium")}</option>
                          <option value="high">{tr("confidence.high")}</option>
                        </select>
                      </div>
                    )}

                    <div className="border border-blue-100 bg-blue-50 p-4 mb-4">
                      <p className="text-sm font-medium text-blue-900 mb-2">{tr("ranges.weightingTitle")}</p>
                      <p className="text-xs text-blue-800 mb-4">
                        {tr("ranges.weightingDescription")}
                      </p>

                      {methodGroup !== "point_method" && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <input type="number" value={draft.ownResultsRecentCount || ""} onChange={(e) => updateBlockDraft(block.key, { ownResultsRecentCount: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.ownRecentCount")} />
                            <input type="number" value={draft.ownResultsRecentValue || ""} onChange={(e) => updateBlockDraft(block.key, { ownResultsRecentValue: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.ownRecentMedian")} />
                            <input type="number" value={draft.ownResults3to5Count || ""} onChange={(e) => updateBlockDraft(block.key, { ownResults3to5Count: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.ownOldCount")} />
                            <input type="number" value={draft.ownResults3to5Value || ""} onChange={(e) => updateBlockDraft(block.key, { ownResults3to5Value: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.ownOldMedian")} />
                          </div>

                          {methodGroup === "analogy_cup" && (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                              <input type="number" value={draft.comparableResultsRecentCount || ""} onChange={(e) => updateBlockDraft(block.key, { comparableResultsRecentCount: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.compRecentCount")} />
                              <input type="number" value={draft.comparableResultsRecentValue || ""} onChange={(e) => updateBlockDraft(block.key, { comparableResultsRecentValue: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.compRecentMedian")} />
                              <input type="number" value={draft.comparableResults3to5Count || ""} onChange={(e) => updateBlockDraft(block.key, { comparableResults3to5Count: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.compOldCount")} />
                              <input type="number" value={draft.comparableResults3to5Value || ""} onChange={(e) => updateBlockDraft(block.key, { comparableResults3to5Value: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.compOldMedian")} />
                            </div>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        <input type="number" value={draft.galleryPriceCap || ""} onChange={(e) => updateBlockDraft(block.key, { galleryPriceCap: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.galleryCap")} />
                        {methodGroup === "analogy_cup" && (
                          <input type="number" value={draft.prudenceDiscountPct || VALUATION_RULES.analogy_cup_without_own.prudenceDiscountPct} onChange={(e) => updateBlockDraft(block.key, { prudenceDiscountPct: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("ranges.prudenceDiscount")} />
                        )}
                      </div>

                      <textarea
                        value={draft.referenceDetail || ""}
                        onChange={(e) => updateBlockDraft(block.key, { referenceDetail: e.target.value })}
                        rows={3}
                        className="w-full border border-gray-200 px-3 py-2 text-sm mt-3"
                        placeholder={tr("ranges.sourcesDetail")}
                      />
                    </div>

                    <input value={draft.notes || ""} onChange={(e) => updateBlockDraft(block.key, { notes: e.target.value })} className="w-full border border-gray-200 px-3 py-2 mb-3" placeholder={tr("ranges.savedNote")} />
                    <div className="border border-gray-100 divide-y divide-gray-100">
                      {block.entries.map((entry) => <div key={entry.artworkId} className="flex justify-between px-3 py-2 text-xs"><span>{entry.title}{entry.inventoryNumber ? ` · Inv. ${entry.inventoryNumber}` : ""}</span><span className="text-gray-400">{entry.mediumLabel} · {entry.sizeBucket}</span></div>)}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col md:flex-row gap-3">
              <button type="button" onClick={saveArtistQuotes} disabled={savingArtistQuotes} className="flex-1 border-2 border-black text-black px-6 py-3 text-sm disabled:opacity-50">{savingArtistQuotes ? tr("actions.saving") : tr("actions.saveQuotes")}</button>
              <button type="button" onClick={() => valuationBlocks.forEach(applyBlockToEntries)} className="flex-1 bg-black text-white px-6 py-3 text-sm">{tr("actions.applyAll")}</button>
            </div>

            <div className="bg-white border border-gray-200 p-6">
              <h2 className="text-2xl font-serif mb-1">{tr("adjustments.title")}</h2>
              <p className="text-sm text-gray-500 mb-5">{tr("adjustments.description")}</p>

              <div className="space-y-5">
                {entries.map((entry) => {
                  const adjustment = computeConditionAdjustedValue(entry);
                  return (
                    <div key={entry.artworkId} className={`border p-5 ${adjustment.isLoss ? "border-red-200 bg-red-50" : "border-gray-200 bg-white"}`}>
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                        <div>
                          <h3 className="font-serif text-lg">{entry.title}</h3>
                          <p className="text-xs text-gray-500">{entry.artist}{entry.inventoryNumber ? ` · Inv. ${entry.inventoryNumber}` : ""} · {entry.mediumLabel} · {entry.sizeBucket}</p>
                          <p className="text-xs text-gray-400 mt-1">{VALUATION_METHODS[entry.methodGroup]?.group} — {VALUATION_METHODS[entry.methodGroup]?.label}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">{tr("adjustments.finalValue")}</p>
                          <p className="text-lg font-semibold text-amber-700">{formatEuro(getFinalValue(entry))}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <input type="number" value={entry.valeurBasse || ""} onChange={(e) => updateEntry(entry.artworkId, { valeurBasse: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("adjustments.low")} />
                        <input type="number" value={entry.valeurHaute || ""} onChange={(e) => updateEntry(entry.artworkId, { valeurHaute: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("adjustments.high")} />
                        <input type="number" value={entry.valeurRetenue || ""} onChange={(e) => updateEntry(entry.artworkId, { valeurRetenue: e.target.value })} className="border-2 border-amber-400 px-3 py-2" placeholder={tr("adjustments.retained")} />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                        <select value={entry.conditionStatus || "bon"} onChange={(e) => updateEntry(entry.artworkId, { conditionStatus: e.target.value })} className="border border-gray-200 px-3 py-2">
                          {CONDITION_OPTIONS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                        </select>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={Boolean(entry.hasAuthenticityCertificate)} onChange={(e) => updateEntry(entry.artworkId, { hasAuthenticityCertificate: e.target.checked })} /> {tr("adjustments.certificate")}</label>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={Boolean(entry.hasArtistProvenance)} onChange={(e) => updateEntry(entry.artworkId, { hasArtistProvenance: e.target.checked })} /> {tr("adjustments.provenance")}</label>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={Boolean(entry.hasImpeccableFollowUp)} onChange={(e) => updateEntry(entry.artworkId, { hasImpeccableFollowUp: e.target.checked })} /> {tr("adjustments.followUp")}</label>
                      </div>

                      <div className="bg-gray-50 border border-gray-100 p-3 text-xs text-gray-600 mb-4">{adjustment.detail}</div>

                      <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={Boolean(entry.expertOverrideEnabled)} onChange={(e) => updateEntry(entry.artworkId, { expertOverrideEnabled: e.target.checked })} /> {tr("adjustments.expertOverride")}</label>
                      {entry.expertOverrideEnabled && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                          <input type="number" value={entry.expertOverrideValue || ""} onChange={(e) => updateEntry(entry.artworkId, { expertOverrideValue: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("adjustments.forcedValue")} />
                          <input value={entry.expertOverrideReason || ""} onChange={(e) => updateEntry(entry.artworkId, { expertOverrideReason: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("adjustments.overrideReason")} />
                        </div>
                      )}

                      <div className="mb-4">
                        <p className="text-xs font-medium text-gray-600 mb-2">{tr("adjustments.sources")}</p>
                        <div className="flex flex-wrap gap-2">
                          {SOURCES.map((source) => <button key={source.key} type="button" onClick={() => toggleSource(entry.artworkId, source.key)} className={`text-xs border px-2 py-1 ${entry.sourcesConsultees?.includes(source.key) ? "bg-black text-white border-black" : "bg-white border-gray-200"}`}>{source.label}</button>)}
                        </div>
                      </div>

                      <textarea value={entry.justification || ""} onChange={(e) => updateEntry(entry.artworkId, { justification: e.target.value })} rows={2} className="w-full border border-gray-200 px-3 py-2 text-sm" placeholder={tr("adjustments.justification")} />
                    </div>
                  );
                })}
              </div>
            </div>

            {lossEntries.length > 0 && (
              <div className="bg-red-50 border border-red-200 p-5">
                <h3 className="font-semibold text-red-900 mb-2">{tr("losses.title")}</h3>
                <p className="text-xs text-red-800 mb-3">{tr("losses.description")}</p>
                {lossEntries.map((entry) => <p key={entry.artworkId} className="text-sm text-red-900">{entry.title} · {entry.artist}{entry.inventoryNumber ? ` · Inv. ${entry.inventoryNumber}` : ""}</p>)}
              </div>
            )}

            <div className="bg-black text-white p-6 flex items-center justify-between">
              <span>{tr("totalAdjusted")}</span>
              <strong className="text-2xl">{formatEuro(totalActive)}</strong>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">{tr("generalNotes")}</label>
              <textarea value={rapportNotes} onChange={(e) => setRapportNotes(e.target.value)} rows={4} className="w-full border border-gray-200 px-3 py-2" />
            </div>

            <div className="flex flex-col md:flex-row gap-3">
              <button type="button" onClick={() => setStep(3)} className="border border-gray-300 px-6 py-3 text-sm">{tr("actions.backToArtists")}</button>
              <button type="button" onClick={saveArtworks} disabled={savingArtworks} className="bg-amber-700 text-white px-6 py-3 text-sm disabled:opacity-50">{savingArtworks ? tr("actions.saving") : tr("actions.saveArtworks")}</button>
              <button type="button" onClick={exportPDF} className="bg-black text-white px-6 py-3 text-sm">{tr("actions.exportPdf")}</button>
              <button type="button" onClick={exportWord} className="border-2 border-black text-black px-6 py-3 text-sm">{tr("actions.exportWord")}</button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
